import { StorageHelper } from '@smg-real-estate/hg-search-libs';
import { unref } from 'vue';
import 'whatwg-fetch';

import { ApiResponseError } from '@/api/ApiResponseError';
import { UnauthenticatedError } from '@/api/UnauthenticatedError';
import auth0 from '@/auth/auth0Plugin';
import { captureErrorOnce } from '@/helpers/errorUtils';
import { EnvBuildTarget, TargetPlatform } from '@/services/envService';
import { NetworkError } from './NetworkError';

interface CreateRequest {
  method: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  credentials?: RequestCredentials;
  headers?: HeadersInit;
  /**
   * Statuses the caller handles as a normal outcome. They still throw an `ApiResponseError`,
   * but are not reported to RUM.
   */
  expectedStatuses?: number[];
}

/**
 * Wraps the fetch API to include authentication headers and error handling.
 */
export function createRequest({
  method,
  credentials = 'include',
  headers = {},
  expectedStatuses = [],
}: CreateRequest) {
  // eslint-disable-next-line max-statements
  return async (endpoint: string, body?: string, customHeaders = {}): Promise<Response> => {
    let accessToken = null;
    let guestToken = null;
    if (unref(auth0.isAuthenticated)) {
      accessToken = await auth0.getAccessTokenSilently();
    } else {
      const storageHelper = new StorageHelper('insertion-funnel');
      guestToken = storageHelper.getLocalStorageItem('guestToken');
    }

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method,
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...platformHeader,
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(guestToken && { 'X-Guest-Token': `${guestToken}` }),
          ...headers,
          ...customHeaders,
        },
        credentials,
        body,
      });
    } catch {
      const error = new NetworkError(method, endpoint, 'Failed to fetch');
      captureErrorOnce(error);
      throw error;
    }

    if (response.ok) {
      return response;
    }

    if (response.status === 403 || response.status === 401) {
      throw new UnauthenticatedError(
        `The user is not authenticated: ${response.status}`,
        response.status,
      );
    }

    let message = `API endpoint ${endpoint} responded with an error code: ${response.status}`;
    if (response.statusText) {
      message += `. Error=${response.statusText}`;
    }
    const error = new ApiResponseError(message, response.status, await readErrorName(response));
    if (!expectedStatuses.includes(response.status)) {
      captureErrorOnce(error, { endpoint, method, status: response.status });
    }

    throw error;
  };
}

async function readErrorName(response: Response): Promise<string | undefined> {
  try {
    const body = await response.clone().json();
    return typeof body?.name === 'string' ? body.name : undefined;
  } catch {
    return undefined;
  }
}

const platformHeader = import.meta.env.VUE_APP_STAGE === 'personal' && {
  'X-Smg-Platform':
    EnvBuildTarget[TargetPlatform.IS24] === import.meta.env.VUE_APP_BUILD_TARGET
      ? TargetPlatform.IS24
      : TargetPlatform.HOMEGATE,
};

export const get = createRequest({ method: 'GET' });
export const post = createRequest({ method: 'POST' });
export const put = createRequest({ method: 'PUT' });
export const deleteRequest = createRequest({ method: 'DELETE' });
export const patch = createRequest({ method: 'PATCH' });
